import { endAdminAccessTab } from '@/auth/admin-access';
import {
  ADMIN_ACCESS_ENDED_PATH,
  leaveEndedAdminAccess,
} from '@/auth/admin-access-navigation';
import { getAdminAccessState } from '@/auth/auth-storage';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

/**
 * 대리 접속 탭의 모든 화면 위에 고정하는 배너.
 * 누구 계정으로 보고 있는지 한눈에 알게 하고, 남은 시간이 0 이 되면 종료 화면으로 보낸다.
 */
export const AdminAccessBanner = () => {
  const navigate = useNavigate();
  // 화면을 옮길 때마다 다시 읽는다. 다른 처리(401 등)가 이 탭을 끝냈을 수 있다.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const state = getAdminAccessState();
  const remainingMs = useRemainingMs(
    state?.status === 'ACTIVE' ? state.info.expiresAt : null,
  );
  const [isEnding, setIsEnding] = useState(false);

  const isEnded = state?.status === 'ENDED';
  useEffect(() => {
    if (isEnded && pathname !== ADMIN_ACCESS_ENDED_PATH) {
      navigate({ to: ADMIN_ACCESS_ENDED_PATH, replace: true });
    }
  }, [isEnded, pathname, navigate]);

  const isActive = state?.status === 'ACTIVE';
  useEffect(() => {
    if (isActive) return keepSameSiteLinksInThisTab();
  }, [isActive]);

  const isTimeUp = remainingMs !== null && remainingMs <= 0;
  useEffect(() => {
    if (isTimeUp) leaveEndedAdminAccess();
  }, [isTimeUp]);

  if (state?.status !== 'ACTIVE') return null;

  const { info } = state;
  return (
    <div className="print:hidden sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-b-2 border-amber-500 bg-amber-100 px-5 py-2.5 text-sm text-amber-900">
      <span className="font-bold">⚠️ 대리 접속 중</span>
      <span>
        <b>{info.targetName}</b> ({info.targetEmail}) 계정으로 보고 있습니다
      </span>
      <span className="text-amber-800">
        접속자 {info.impersonatorName || '-'} · 사유: {info.reasonLabel}
      </span>
      <span className="font-bold tabular-nums">
        남은 시간 {formatRemaining(remainingMs ?? 0)}
      </span>
      <button
        type="button"
        disabled={isEnding}
        onClick={() => {
          setIsEnding(true);
          endAdminAccessTab().catch(() => setIsEnding(false));
        }}
        className="cursor-pointer rounded-lg bg-amber-900 px-3 py-1 font-semibold text-white disabled:opacity-60"
      >
        {isEnding ? '종료 중…' : '종료'}
      </button>
    </div>
  );
};

function useRemainingMs(expiresAt: string | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt]);
  return expiresAt ? new Date(expiresAt).getTime() - now : null;
}

function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * 대리 접속 상태는 이 탭의 sessionStorage 에만 있어서, 새 탭은 원래 탭의 CPM 로그인으로 열린다.
 * 대상 화면인 줄 알고 CPM 계정으로 보거나 저장하지 않도록, 같은 사이트 주소는 이 탭에서 연다.
 * 다른 사이트(외부 링크)는 그대로 새 탭으로 연다.
 */
function keepSameSiteLinksInThisTab(): () => void {
  const toSameSiteUrl = (href: string | URL | undefined | null) => {
    if (!href) return null;
    // 형식이 깨진 주소는 해석에서 예외가 난다. 가로채지 않고 브라우저 원래 동작에 맡긴다.
    try {
      const url = new URL(href, window.location.href);
      return url.origin === window.location.origin ? url : null;
    } catch {
      return null;
    }
  };

  const onLinkClick = (event: MouseEvent) => {
    const anchor = (event.target as Element | null)?.closest?.('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const opensNewTab =
      anchor.target === '_blank' ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.button === 1;
    const url = toSameSiteUrl(anchor.href);
    if (opensNewTab && url) {
      event.preventDefault();
      event.stopPropagation();
      window.location.assign(url.href);
    }
  };

  const originalOpen = window.open;
  window.open = (href, target, features) => {
    const url = toSameSiteUrl(href);
    if (url) {
      window.location.assign(url.href);
      return window;
    }
    return originalOpen.call(window, href, target, features);
  };

  // 캡처 단계에서 받아, 라우터 링크가 새 탭을 열기 전에 가로챈다.
  document.addEventListener('click', onLinkClick, true);
  document.addEventListener('auxclick', onLinkClick, true);
  return () => {
    document.removeEventListener('click', onLinkClick, true);
    document.removeEventListener('auxclick', onLinkClick, true);
    window.open = originalOpen;
  };
}
